import React from 'react';
import Tab from 'react-bootstrap/Tab';
import Tabs from 'react-bootstrap/Tabs';
import PropTypes from 'prop-types';

import Loading from '../Loading';
import Error from '../Error';
import HintTextWithTooltip from '../HintTextWithTooltip';

import UsageTablePerMonth from '../UsageTablePerMonth';
import UsageTablePerService from '../UsageTablePerService';
import UsageTableTotalUsers from '../UsageTableTotalUsers';

import resources from '../../utils/resources';
import csvDownloader from '../../utils/csvDownloader';
import noop from '../../utils/noop';
import { getTotalUsersByTenant } from '../../selectors/usage';

const csvColumns = {
  month: 'Month',
  tenant: 'Tenant',
  client_name: 'Application',
  users_social: 'Social',
  users_password: 'Password',
  users_passwordless: 'Passwordless',
  users_enterprise: 'Enterprise',
  active_users: 'Total Active'
};

const UsageReport = ({
  recordsByMonth,
  recordsByService,
  recordsTotalUsers,
  loadingActiveUsers,
  errorActiveUsers,
  loadingTotalUsers,
  errorTotalUsers,
  activePageActiveUsers,
  activePageTotalUsers,
  paginateActiveUsers,
  paginateTotalUsers,
  filterActiveUsers,
  filterTotalUsers,
  filteringActiveUsers,
  filteringTotalUsers
}) => {
  const resetActivePage = () => paginateActiveUsers();
  const resetTotalPage = () => paginateTotalUsers();
  const resetActiveUsersFilter = () => {
    filterActiveUsers()();
  };
  const resetTotalUsersFilter = () => {
    filterTotalUsers()();
  };

  return (
    <div id="usage-report-tabs">
      <Tabs defaultActiveKey={1} transition>
        <Tab
          eventKey={1}
          title="Active Users"
          unmountOnExit
          onExiting={resetActivePage}
        >
          <Loading show={loadingActiveUsers}>
            <Error message={errorActiveUsers}>
              <div>
                <p>
                  {resources.ACTIVE_USERS_PER_MONTH[0]}
                  <HintTextWithTooltip
                    title={resources.ACTIVE_USERS_TOOLTIP}
                    text={resources.ACTIVE_USERS_PER_MONTH[1]}
                  />
                  {resources.ACTIVE_USERS_PER_MONTH[2]}
                  <a
                    href="#"
                    onClick={csvDownloader(
                      recordsByMonth,
                      'recordsByMonth',
                      {},
                      csvColumns
                    )}
                  >
                    Download Report in CSV Format.
                  </a>
                </p>
                <UsageTablePerMonth
                  records={recordsByMonth}
                  activePage={activePageActiveUsers}
                  paginate={paginateActiveUsers}
                />
              </div>
            </Error>
          </Loading>
        </Tab>
        <Tab
          eventKey={2}
          title="Active Users by Tenant"
          unmountOnExit
          onExiting={resetActivePage}
          onEntering={resetActiveUsersFilter}
        >
          <Loading show={loadingActiveUsers}>
            <Error message={errorActiveUsers}>
              <div>
                <p>
                  {resources.ACTIVE_USERS_PER_MONTH_AND_SERVICE[0]}
                  <HintTextWithTooltip
                    title={resources.ACTIVE_USERS_TOOLTIP}
                    text={resources.ACTIVE_USERS_PER_MONTH_AND_SERVICE[1]}
                  />
                  {resources.ACTIVE_USERS_PER_MONTH_AND_SERVICE[2]}
                  <a
                    href="#"
                    onClick={csvDownloader(
                      recordsByService,
                      'recordsByTenant',
                      filteringActiveUsers,
                      csvColumns
                    )}
                  >
                    Download Report in CSV Format.
                  </a>
                </p>
                <UsageTablePerService
                  records={recordsByService}
                  activePage={activePageActiveUsers}
                  paginate={paginateActiveUsers}
                  filter={filterActiveUsers}
                  filtering={filteringActiveUsers}
                />
              </div>
            </Error>
          </Loading>
        </Tab>
        <Tab
          eventKey={4}
          title="Total Users by Tenant"
          unmountOnExit
          onExiting={resetTotalPage}
          onEntering={resetTotalUsersFilter}
        >
          <Loading show={loadingTotalUsers}>
            <Error message={errorTotalUsers}>
              <div>
                <p>
                  {resources.TOTAL_USERS_PER_MONTH_AND_SERVICE[0]}
                  <HintTextWithTooltip
                    title={resources.TOTAL_USERS_TOOLTIP}
                    text={resources.TOTAL_USERS_PER_MONTH_AND_SERVICE[1]}
                  />
                  {resources.TOTAL_USERS_PER_MONTH_AND_SERVICE[2]}
                  <a
                    href="#"
                    onClick={csvDownloader(
                      getTotalUsersByTenant(recordsTotalUsers),
                      'totalUsersByTenant',
                      filteringTotalUsers,
                      {
                        month: 'Month',
                        tenant: 'Tenant',
                        all_external_users: 'External Users',
                        all_internal_users: 'Employees',
                        all_users: 'Total Users'
                      },
                      [
                        'month',
                        'tenant',
                        'all_external_users',
                        'all_internal_users',
                        'all_users'
                      ]
                    )}
                  >
                    Download Report in CSV Format.
                  </a>
                </p>
                <UsageTableTotalUsers
                  records={getTotalUsersByTenant(recordsTotalUsers)}
                  activePage={activePageTotalUsers}
                  paginate={paginateTotalUsers}
                  filter={filterTotalUsers}
                  filtering={filteringTotalUsers}
                />
              </div>
            </Error>
          </Loading>
        </Tab>
      </Tabs>
    </div>
  );
};

UsageReport.propTypes = {
  recordsByMonth: PropTypes.array.isRequired,
  recordsByClient: PropTypes.array.isRequired,
  recordsByService: PropTypes.array.isRequired,
  recordsTotalUsers: PropTypes.array.isRequired,
  loadingActiveUsers: PropTypes.bool.isRequired,
  loadingTotalUsers: PropTypes.bool.isRequired,
  errorActiveUsers: PropTypes.string,
  errorTotalUsers: PropTypes.string,
  activePageActiveUsers: PropTypes.number.isRequired,
  activePageTotalUsers: PropTypes.number.isRequired,
  paginateActiveUsers: PropTypes.func.isRequired,
  paginateTotalUsers: PropTypes.func.isRequired,
  filterActiveUsers: PropTypes.func.isRequired,
  filterTotalUsers: PropTypes.func.isRequired,
  filteringActiveUsers: PropTypes.object.isRequired,
  filteringTotalUsers: PropTypes.object.isRequired,
};

UsageReport.defaultProps = {
  activePageActiveUsers: 1,
  activePageTotalUsers: 1,
  paginateActiveUsers: noop,
  paginateTotalUsers: noop,
  filterActiveUsers: () => noop,
  filterTotalUsers: () => noop,
  filteringActiveUsers: {},
  filteringTotalUsers: {},
  loadingActiveUsers: false,
  loadingTotalUsers: false,
};

UsageReport.displayName = 'UsageReport';

export default UsageReport;
